/**
 * canvasAssuranceStyles - the stylesheet half of package 8 (canvas honesty).
 *
 * `canvasAssurance.ts` decides the facts (`_risk_state`, `_attr_cert` stamped
 * on node data); this file decides how they look. GraphCanvas splices the
 * entries returned here into its stylesheet and uses the two badge-URI
 * helpers in the base node's `background-image` slots 4 and 5.
 *
 * Both badges are 88px-explicit SVGs on the same rules as the flag badge in
 * GraphCanvas (explicit pixel dims, no filters) so they stay crisp at zoom.
 */
import { BORDER_NEUTRAL } from './canvasAssurance';
import { CanvasNodeIntent } from '../types/canvasIntent';

const TRANSPARENT_PX = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'/>`,
)}`;

function glyphBadge(fill: string, stroke: string, glyph: string, dashed = false): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='88' height='88' viewBox='0 0 22 22'>` +
      `<circle cx='11' cy='11' r='10.5' fill='#ffffff'/>` +
      `<circle cx='11' cy='11' r='9' fill='${fill}' stroke='${stroke}' stroke-width='1.6'${dashed ? " stroke-dasharray='3 2'" : ''}/>` +
      `<text x='11' y='15' text-anchor='middle' font-family='Arial,sans-serif' font-size='11' font-weight='700' fill='#ffffff'>${glyph}</text>` +
      `</svg>`,
  )}`;
}

/** Slot 4 (bottom centre): attribution certainty. Nothing for unspecified or none. */
const CERT_BADGES: Record<string, string> = {
  verified: glyphBadge('#15803d', '#166534', 'V'),
  provider: glyphBadge('#2563eb', '#1d4ed8', 'P'),
  heuristic: glyphBadge('#d97706', '#b45309', 'H', true),
  cluster: glyphBadge('#d97706', '#b45309', 'C', true),
  probable: glyphBadge('#7c3aed', '#6d28d9', '~', true),
};

/** Slot 5 (top centre): "?" for a node whose risk was never assessed. */
const NOT_ASSESSED_BADGE = glyphBadge('#64748b', '#475569', '?', true);

export const getCertaintyBadgeUri = (ele: any): string =>
  CERT_BADGES[ele.data('_attr_cert')] ?? TRANSPARENT_PX;

export const getNotAssessedBadgeUri = (ele: any): string =>
  ele.data('_risk_state') === 'not_assessed' ? NOT_ASSESSED_BADGE : TRANSPARENT_PX;

/**
 * Stylesheet entries. Order matters: these must come after the canvas-intent
 * node rules so the unassessed look wins over the tier colour, and before
 * `node:selected`.
 */
export function buildAssuranceNodeStyles(): any[] {
  return [
    // Never assessed: neutral grey dashed border. The subject (root) keeps
    // its own amber marker; the "?" badge still tells the truth there.
    {
      selector: `node[_risk_state = "not_assessed"][canvas_intent != "${CanvasNodeIntent.TARGET_ROOT}"]`,
      style: {
        'border-color': BORDER_NEUTRAL,
        'border-style': 'dashed' as const,
      },
    },
  ];
}
